<bn-care-plan-editor-shell
  headingId="outcome-editor-title"
  [heading]="
    removing()
      ? 'Remove outcome'
      : outcome
        ? writable()
          ? 'Edit outcome'
          : 'View outcome'
        : 'Record outcome'
  "
>
  <div carePlanEditorTitle class="bn-outcome-editor__title">
    @if (removing()) {
      <p class="bn-outcome-editor__description">{{ outcome?.name }}</p>
      @if (outcome?.description) {
        <p class="bn-outcome-editor__hint">{{ outcome?.description }}</p>
      }
      <p class="bn-outcome-editor__hint">
        This outcome will stay marked Removed and will no longer count toward
        financial impact.
      </p>
    } @else {
      <label for="outcome-name"
        >Outcome name
        @if (!linkOnly()) {
          <span aria-hidden="true">*</span>
        }
      </label>
      <input
        id="outcome-name"
        class="bn-outcome-editor__name-control"
        type="text"
        maxlength="250"
        [required]="!linkOnly()"
        placeholder="What was the positive impact or change?"
        [disabled]="saving() || store.movePending() || !writable()"
        [ngModel]="name()"
        (ngModelChange)="name.set($event)"
        [attr.aria-invalid]="attempted() && !linkOnly() && !!nameError()"
        aria-describedby="outcome-name-help"
      />
      <div id="outcome-name-help" class="bn-outcome-editor__help">
        @if (attempted() && !linkOnly() && nameError(); as error) {
          <span class="bn-outcome-editor__error" role="alert">{{ error }}</span>
        }
        <span>{{ name().length }}/250</span>
      </div>
      <label for="outcome-description">Outcome description (optional)</label>
      <textarea
        id="outcome-description"
        pTextarea
        rows="3"
        maxlength="500"
        placeholder="What changed and how it helped this client"
        [disabled]="saving() || store.movePending() || !writable()"
        [ngModel]="description()"
        (ngModelChange)="description.set($event)"
        [attr.aria-invalid]="attempted() && !!descriptionError()"
        aria-describedby="outcome-description-error"
      ></textarea>
      @if (attempted() && descriptionError(); as error) {
        <p
          id="outcome-description-error"
          class="bn-outcome-editor__error"
          role="alert"
        >
          {{ error }}
        </p>
      }
    }
  </div>
  <div carePlanEditorMetadata class="bn-outcome-editor__metadata">
    @if (removing()) {
      <div class="bn-outcome-editor__title">
        <label for="outcome-removal-reason"
          >Reason for removal <span aria-hidden="true">*</span></label
        >
        <textarea
          id="outcome-removal-reason"
          pTextarea
          rows="4"
          maxlength="500"
          required
          [disabled]="saving() || store.movePending() || !writable()"
          placeholder="Wrong calculation, option didn’t work for client…"
          [ngModel]="reason()"
          (ngModelChange)="reason.set($event)"
          [attr.aria-invalid]="removalAttempted() && !!reasonError()"
          aria-describedby="outcome-reason-help"
        ></textarea>
        <div id="outcome-reason-help" class="bn-outcome-editor__help">
          @if (removalAttempted() && reasonError(); as error) {
            <span class="bn-outcome-editor__error" role="alert">{{
              error
            }}</span>
          }
          <span>{{ reason().length }}/500</span>
        </div>
      </div>
    } @else {
      <bn-care-plan-editor-row
        icon="pi-align-left"
        label="Outcome type"
        inputId="outcome-type"
        [required]="true"
      >
        <p-select
          inputId="outcome-type"
          [options]="types()"
          optionLabel="label"
          optionValue="code"
          appendTo="body"
          [fluid]="true"
          placeholder="Select outcome type"
          [ngModel]="typeCode()"
          (ngModelChange)="typeCode.set($event)"
          [disabled]="saving() || store.movePending() || !writable()"
          [required]="true"
          [invalid]="attempted() && !!typeError()"
          ariaLabel="Outcome type"
        />
        @if (attempted() && typeError(); as error) {
          <p carePlanEditorError class="bn-outcome-editor__error" role="alert">
            {{ error }}
          </p>
        }
      </bn-care-plan-editor-row>
      <bn-care-plan-editor-row
        icon="pi-folder"
        label="Case"
        inputId="outcome-case"
      >
        <p-select
          inputId="outcome-case"
          [options]="caseChoices()"
          optionLabel="label"
          optionValue="value"
          optionDisabled="disabled"
          appendTo="body"
          [fluid]="true"
          [showClear]="true"
          placeholder="No case"
          [ngModel]="selectedCaseId()"
          (ngModelChange)="selectCase($event)"
          [disabled]="
            !outcome || saving() || store.movePending() || !writable()
          "
          ariaLabel="Link outcome to a case"
        />
        @if (noEligibleCases()) {
          <p class="bn-outcome-editor__hint" role="status">
            This client has no cases you can link to.
          </p>
        }
        @if (caseError(); as error) {
          <p carePlanEditorError class="bn-outcome-editor__error" role="alert">
            {{ error }}
          </p>
        }
      </bn-care-plan-editor-row>
      @if (selectedCaseId() !== null) {
        <bn-care-plan-editor-row
          icon="pi-link"
          label="Linked options"
          inputId="outcome-options"
          [required]="true"
        >
          <p-multiSelect
            inputId="outcome-options"
            [options]="optionChoices()"
            optionLabel="label"
            optionValue="id"
            optionDisabled="disabled"
            appendTo="body"
            [fluid]="true"
            [filter]="true"
            [showToggleAll]="false"
            placeholder="Select linked options"
            [ngModel]="linkedOptions()"
            (ngModelChange)="linkedOptions.set($event)"
            [disabled]="
              saving() ||
              store.movePending() ||
              !writable() ||
              ((clientScoped || reassigning()) && store.linkOptionsLoading())
            "
            [required]="true"
            [invalid]="attempted() && !!optionsError()"
            ariaLabel="Linked options"
          />
          @if (attempted() && optionsError(); as error) {
            <p
              carePlanEditorError
              class="bn-outcome-editor__error"
              role="alert"
            >
              {{ error }}
            </p>
          }
        </bn-care-plan-editor-row>
        @if (clientScoped || reassigning()) {
          @if (store.linkOptionsLoading()) {
            <p role="status">Loading case options…</p>
          } @else if (store.linkOptionsError(); as error) {
            <div role="alert">
              <p class="bn-outcome-editor__error">{{ error }}</p>
              <button
                type="button"
                (click)="store.loadCaseOptions(selectedCaseId())"
              >
                Retry case options
              </button>
            </div>
          } @else if (store.options().length === 0) {
            <p class="bn-outcome-editor__hint">
              This case has no options. Add an option in its Plan tab or choose
              another case.
            </p>
          }
        }
      }
      @if (outcome) {
        <bn-care-plan-editor-row
          [icon]="clientScoped ? 'pi-lock' : 'pi-dollar'"
          label="ATI amount"
          inputId="outcome-ati"
        >
          <p-inputnumber
            inputId="outcome-ati"
            class="bn-outcome-editor__amount"
            [fluid]="true"
            mode="currency"
            currency="USD"
            locale="en-US"
            [minFractionDigits]="2"
            [maxFractionDigits]="2"
            placeholder="Optional amount in USD"
            [readonly]="clientScoped"
            [disabled]="
              clientScoped || saving() || store.movePending() || !writable()
            "
            [ngModel]="atiAmount()"
            (ngModelChange)="ati.set($event == null ? '' : '' + $event)"
            [invalid]="attempted() && !!atiError()"
            ariaDescribedBy="outcome-ati-help"
          />
          <div carePlanEditorError id="outcome-ati-help">
            @if (attempted() && atiError(); as error) {
              <p class="bn-outcome-editor__error" role="alert">{{ error }}</p>
            }
            <p class="bn-outcome-editor__hint">
              @if (clientScoped) {
                ATI is read-only in Unlinked Outcomes.
              } @else {
                Leave blank if unknown. Enter 0 for no financial impact.
              }
            </p>
          </div>
        </bn-care-plan-editor-row>
      }
    }
  </div>
  @if (!writable()) {
    <p class="bn-outcome-editor__hint" role="status">
      This outcome is view-only.
    </p>
  }
  @if (outcome?.invalidated) {
    <p class="bn-outcome-editor__hint">
      Removed: {{ outcome?.invalidatedReason }}
    </p>
  }
  @if (store.referenceError(); as error) {
    <div role="alert">
      <p class="bn-outcome-editor__error">{{ error }}</p>
      <button type="button" (click)="store.loadReferences(true)">
        Retry outcome choices
      </button>
    </div>
  }
  @if (saveError(); as error) {
    <p class="bn-outcome-editor__error" role="alert">{{ error }}</p>
  }
  @if (!removing()) {
    @if (selectedCaseInfo(); as info) {
      <section
        class="bn-outcome-editor__case-info"
        aria-labelledby="outcome-case-info-title"
      >
        <h3>
          <button
            type="button"
            id="outcome-case-info-title"
            class="bn-outcome-editor__case-info-toggle"
            [attr.aria-expanded]="caseInfoOpen()"
            aria-controls="outcome-case-info-details"
            (click)="caseInfoOpen.set(!caseInfoOpen())"
          >
            Case info
            <i
              class="pi"
              [class.pi-angle-down]="!caseInfoOpen()"
              [class.pi-angle-up]="caseInfoOpen()"
              aria-hidden="true"
            ></i>
          </button>
        </h3>
        @if (caseInfoOpen()) {
          <div
            id="outcome-case-info-details"
            class="bn-outcome-editor__case-info-details"
          >
            <dl>
              <div>
                <dt>Domain</dt>
                <dd>{{ info.domain }}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{{ info.status }}</dd>
              </div>
              <div>
                <dt>Opened date</dt>
                <dd>
                  {{
                    info.openedAt ? (info.openedAt | date: 'mediumDate') : '—'
                  }}
                </dd>
              </div>
            </dl>
          </div>
        }
      </section>
    }
  }
  <div carePlanEditorFooter class="bn-outcome-editor__footer">
    @if (removing()) {
      <p-button
        label="Back"
        [text]="true"
        [disabled]="saving()"
        (onClick)="back()"
      />
      <p-button
        label="Confirm removal"
        severity="danger"
        [rounded]="true"
        [loading]="saving()"
        [disabled]="saving() || store.movePending() || !writable()"
        (onClick)="confirmRemoval()"
      />
    } @else {
      @if (outcome && writable() && !clientScoped && !store.movePending()) {
        <button
          type="button"
          class="bn-outcome-editor__remove"
          [disabled]="saving()"
          (click)="startRemoval()"
        >
          Remove outcome
        </button>
      }
      <p-button
        [label]="writable() ? 'Cancel' : 'Close'"
        [text]="true"
        [disabled]="saving()"
        (onClick)="tryClose()"
      />
      @if (writable()) {
        <p-button
          [label]="
            unlinking()
              ? 'Unlink from case'
              : outcome
                ? linkOnly()
                  ? 'Link to case'
                  : 'Save'
                : 'Calculate ATI in Navi'
          "
          [icon]="outcome ? undefined : 'pi pi-comment'"
          [rounded]="true"
          [loading]="saving()"
          [disabled]="!canSubmit()"
          (onClick)="submit()"
        />
      }
    }
  </div>
</bn-care-plan-editor-shell>
<p-confirmDialog />
